import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ThemeGallery from "./storeSetting/ThemeGallery";
import { storeSettingsService } from "../../services/storeSettings.service";
import { getBusiness } from "../../services/auth.service";
import { getStorefrontPath } from "./storefrontPaths";
import { THEME_LAYOUT_PRESETS } from "./storeSetting/ThemeGallery";
import { THEME_IMAGES } from "./themeTemplates/ThemeLayoutHelpers";
const EMPTY_THEME_TEXT = {
  heroEyebrow: "",
  heroTitle: "",
  heroSubtitle: "",
  primaryCtaText: "",
  secondaryCtaText: "",
};

const getThemeText = (theme) => ({
  heroEyebrow: theme?.heroEyebrow || "",
  heroTitle: theme?.heroTitle || "",
  heroSubtitle: theme?.heroSubtitle || "",
  primaryCtaText: theme?.primaryCtaText || "",
  secondaryCtaText: theme?.secondaryCtaText || "",
});

export default function ThemeGalleryPage() {
  const { bussinessUuid, businessName } = useParams();
  const navigate = useNavigate();
  const [storeTheme, setStoreTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingText, setSavingText] = useState(false);
  const { t } = useTranslation();
  const [themeText, setThemeText] = useState(EMPTY_THEME_TEXT);
  const [resolvedBusinessName, setResolvedBusinessName] = useState(businessName || "");
  const [allThemesById, setAllThemesById] = useState({});

  const storefrontUrl = `${getStorefrontPath({
    businessName: resolvedBusinessName,
    bussinessUuid,
  })}/`;

const refreshThemeData = useCallback(async () => {
  try {
    const businessResponse = await getBusiness(bussinessUuid);
    const fetchedBusinessName = businessResponse?.data?.businessName || "";
    if (fetchedBusinessName) {
      setResolvedBusinessName(fetchedBusinessName);
    }

    const response = await storeSettingsService.getStoreSettings(bussinessUuid);
    if (response.success) {
      // Store the full themesById map
      let themesByIdMap = {};
      try {
        const themesByIdData = response.data.themesById || {};
        if (typeof themesByIdData.get === 'function') {
          for (const [key, value] of themesByIdData.entries()) {
            themesByIdMap[key] = value;
          }
        } else {
          themesByIdMap = themesByIdData;
        }
      } catch (e) {
        console.warn('Failed to resolve themesById', e);
      }
      
      setAllThemesById(themesByIdMap);

        // Load the currently active theme
      const activeTheme = response.data.theme || {};
        const activeThemeId = activeTheme.themeId;
        let themeToUse = activeTheme;
      
        // Try to get saved version from themesById
        if (activeThemeId && themesByIdMap[activeThemeId]) {
          themeToUse = themesByIdMap[activeThemeId];
        }
      
      setStoreTheme(themeToUse);
      setThemeText(getThemeText(themeToUse));
    }
  } catch (_e) {
    toast.error("Failed to load theme settings");
  } finally {
    setLoading(false);
  }
}, [bussinessUuid]);

  // Load on mount
  useEffect(() => {
    refreshThemeData();
  }, [bussinessUuid, refreshThemeData]);

  // Refresh when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshThemeData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshThemeData]);

  const handleOpenStorefront = () => {
    window.open(storefrontUrl, "_blank", "noopener,noreferrer");
  };

  const handlePreviewTheme = (theme) => {
    const previewPath = businessName
      ? `/${businessName}/${bussinessUuid}/online-store/themes/preview/${theme.id}`
      : `/${bussinessUuid}/online-store/themes/preview/${theme.id}`;
    navigate(previewPath, { state: { theme } });
  };

// In ThemeGalleryPage.jsx, replace the handleApplyTheme function and the prop passing:

const handleApplyTheme = async (themeToApply) => {
  try {
    // Add logging to see what's being passed
    console.log('Applying theme:', themeToApply);
    console.log('Theme ID:', themeToApply.themeId || themeToApply.id);
    
    // Ensure themePreviewImage is set before saving
    if (!themeToApply.themePreviewImage && themeToApply.themeId) {
      // Try to get image from imported SHOPIFY_THEMES if needed
      const { SHOPIFY_THEMES } = await import("./storeSetting/ThemeGallery");
      const themeFromList = SHOPIFY_THEMES.find(t => t.id === themeToApply.themeId);
      if (themeFromList) {
        themeToApply.themePreviewImage = themeFromList.image;
      }
    }
    
    // CRITICAL FIX: Ensure both themeId AND id are set correctly
    const themeId = themeToApply.themeId || themeToApply.id;
    if (themeId) {
      themeToApply.themeId = themeId;
      themeToApply.id = themeId;
    }
    
    const response = await storeSettingsService.updateTheme(bussinessUuid, themeToApply);
    console.log('Save response:', response);
    
    if (response.success) {
      setStoreTheme(themeToApply);
      setThemeText(getThemeText(themeToApply));
      
      // Update the themesById map with the complete theme including image
      const themeId = themeToApply.themeId;
      if (themeId) {
        setAllThemesById(prev => ({
          ...prev,
          [themeId]: { ...themeToApply, themePreviewImage: themeToApply.themePreviewImage }
        }));
      }
      
      toast.success(response.message || "Theme applied successfully!");
      
      // Open storefront in new tab
      setTimeout(() => {
        window.open(storefrontUrl, "_blank", "noopener,noreferrer");
      }, 500);
    } else {
      toast.error(response.error || "Failed to apply theme");
    }
  } catch (_e) {
    console.error("Error saving theme:", _e);
    toast.error("Error saving theme");
  }
};

// Then in the return statement, pass the correct function name:
return (
  <div className="space-y-6 p-4">
    <ThemeGallery 
      storeTheme={storeTheme} 
      onApplyTheme={handleApplyTheme}  // ← This matches the function name
      onPreview={handlePreviewTheme}
      allThemesById={allThemesById}
    />
  </div>
);
  const handleThemeTextChange = (key, value) => {
    setThemeText((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSaveThemeText = async () => {
    if (!storeTheme) return;

    const nextTheme = {
      ...storeTheme,
      ...themeText,
    };

    try {
      setSavingText(true);
      const response = await storeSettingsService.updateTheme(bussinessUuid, nextTheme);
      if (response.success) {
        setStoreTheme(nextTheme);
        
        // Update the saved version in allThemesById
        const themeId = nextTheme.themeId;
        if (themeId) {
          setAllThemesById(prev => ({
            ...prev,
            [themeId]: nextTheme,
          }));
        }
        
        toast.success(response.message || "Theme text saved successfully!");
      } else {
        toast.error(response.error || "Failed to save theme text");
      }
    } catch (_e) {
      toast.error("Error saving theme text");
    } finally {
      setSavingText(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <ThemeGallery 
        storeTheme={storeTheme} 
        onApplyTheme={handleApplyTheme} 
        onPreview={handlePreviewTheme}
        allThemesById={allThemesById}
      />
    </div>
  );
}